import { useEffect, useRef, useState, useCallback } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import {
  generateSelfServiceToken,
  generateSelfServiceRefreshToken,
} from '../../api/selfService'
import { useSnackbar } from '../../contexts/SnackbarProvider'

// Session key used to remember the last route the embedded (React) app was on,
// so a token refresh / reload restores the user to the same screen.
const LAST_REACT_ROUTE_KEY = 'selfServiceLastReactRoute'
// Refresh the DWH token every 2 minutes (matches the Angular implementation).
const TOKEN_REFRESH_INTERVAL_MS = 2 * 60 * 1000

const SelfService = () => {
  const { showSnackbar } = useSnackbar()
  const iframeRef = useRef(null)

  const [safeUrl, setSafeUrl] = useState('')
  const [showIframe, setShowIframe] = useState(false)
  const [isAuthError, setIsAuthError] = useState(false)

  // Mutable auth/config state kept in refs so the message handler and interval
  // callback always read the latest values without re-registering listeners.
  const currentTokenRef = useRef(null)
  const refreshTokenRef = useRef(null)
  const baseRef = useRef(null) // URL object of the embedded app base
  const allowedOriginRef = useRef(null) // origin used to validate postMessage
  const isLoggedInRef = useRef(false)
  const iframeShowTimeoutRef = useRef(null)

  // Build a safe absolute URL constrained to the embedded app origin.
  const buildUrlFromRoute = useCallback(route => {
    const base = baseRef.current
    if (!base) return ''
    const baseStr = base.toString()
    try {
      const abs = new URL(route, baseStr).toString()
      if (!abs.startsWith(base.origin + '/')) {
        return baseStr
      }
      return abs
    } catch {
      return baseStr
    }
  }, [])

  // Initialise the iframe src from the last remembered route, or the base URL.
  const initIframeUrl = useCallback(() => {
    const base = baseRef.current
    if (!base) return
    const lastRoute = sessionStorage.getItem(LAST_REACT_ROUTE_KEY)
    setSafeUrl(lastRoute ? buildUrlFromRoute(lastRoute) : base.toString())
  }, [buildUrlFromRoute])

  // Post the DWH token into the iframe so the embedded app can authenticate.
  const authenticateIframe = useCallback(() => {
    const token = currentTokenRef.current
    const origin = allowedOriginRef.current
    if (!token || !origin) return

    setTimeout(() => {
      try {
        iframeRef.current?.contentWindow?.postMessage(
          { type: 'EMBED_AUTH', token },
          origin
        )
      } catch (error) {
        showSnackbar(`Error sending authentication: ${error.message}`, 'error')
      }
    }, 500)
  }, [showSnackbar])

  // Exchange the DWH refresh token for a fresh access token.
  const fetchRefreshTokenDWH = useCallback(async () => {
    const refreshToken = refreshTokenRef.current
    if (!refreshToken) {
      showSnackbar('Session expired', 'error')
      setIsAuthError(true)
      setShowIframe(false)
      return
    }

    try {
      const response = await generateSelfServiceRefreshToken(refreshToken)
      if (response?.success) {
        currentTokenRef.current = response.data.access_token
        authenticateIframe()
        setIsAuthError(false)
      } else {
        setIsAuthError(true)
      }
    } catch (err) {
      if (err?.status !== 401) {
        isLoggedInRef.current = false
      }
    }
  }, [authenticateIframe, showSnackbar])

  // Fetch a new DWH token (first login) or refresh an existing session.
  const getToken = useCallback(async () => {
    if (isLoggedInRef.current) {
      // Already logged in -> just refresh.
      fetchRefreshTokenDWH()
      return
    }

    try {
      const response = await generateSelfServiceToken()

      if (response?.success) {
        currentTokenRef.current = response.data.access_token
        refreshTokenRef.current = response.data.refresh_token
        const base = new URL(response.data.dwhBaseUrl)
        baseRef.current = base
        allowedOriginRef.current = base.origin
        initIframeUrl()
        authenticateIframe()
        setIsAuthError(false)
        isLoggedInRef.current = true
      } else if (response?.status === 401) {
        fetchRefreshTokenDWH()
      } else if (response?.status === 403) {
        setIsAuthError(true)
      } else {
        setIsAuthError(true)
      }
    } catch (err) {
      setIsAuthError(true)
      showSnackbar(err?.message || 'Failed to load Self Service', 'error')
    }
  }, [authenticateIframe, fetchRefreshTokenDWH, initIframeUrl, showSnackbar])

  // Handle messages posted back by the embedded app.
  useEffect(() => {
    const handleMessage = event => {
      if (event.origin !== allowedOriginRef.current) return

      const data = event.data
      if (data?.type === 'EMBED_AUTH_RESULT') {
        iframeShowTimeoutRef.current = setTimeout(() => setShowIframe(true), 1000)
      } else if (data?.type === 'AUTH_ERROR') {
        setShowIframe(false)
        getToken()
        if (iframeShowTimeoutRef.current) {
          clearTimeout(iframeShowTimeoutRef.current)
        }
      } else if (data?.type === 'IFRAME_ROUTE_CHANGE') {
        const incoming = data.fullUrl || data.route || '/'
        try {
          const u = new URL(String(incoming), baseRef.current?.toString())
          sessionStorage.setItem(
            LAST_REACT_ROUTE_KEY,
            `${u.pathname}${u.search}${u.hash}`
          )
        } catch {
          /* ignore malformed route */
        }
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [getToken])

  // Initial token fetch + periodic refresh; lock page scroll while embedded.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    document.body.style.overflow = 'hidden'

    getToken()
    const intervalId = setInterval(getToken, TOKEN_REFRESH_INTERVAL_MS)

    return () => {
      clearInterval(intervalId)
      if (iframeShowTimeoutRef.current) {
        clearTimeout(iframeShowTimeoutRef.current)
      }
      document.body.style.overflow = ''
      sessionStorage.removeItem(LAST_REACT_ROUTE_KEY)
    }
  }, [getToken])

  return (
    <Box className="self-service-wrapper" sx={{ height: '100%', width: '100%' }}>
      <Box
        sx={{
          position: 'relative',
          height: 'calc(100vh - 120px)',
          width: '100%',
        }}
      >
        {safeUrl && (
          <iframe
            ref={iframeRef}
            src={safeUrl}
            title="Self Service"
            onLoad={authenticateIframe}
            allow="microphone"
            style={{
              display: showIframe ? 'block' : 'none',
              border: 'none',
              height: '100%',
              width: '100%',
            }}
          />
        )}

        {/* Loading state */}
        {!showIframe && !isAuthError && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Access denied state */}
        {!showIframe && isAuthError && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <Box
              sx={{
                textAlign: 'center',
                maxWidth: 480,
                p: 4,
                borderRadius: 2,
                border: '1px solid var(--border-strong)',
                backgroundColor: 'var(--surface, #fff)',
              }}
            >
              <ErrorOutlineIcon color="error" sx={{ fontSize: 56, mb: 1 }} />
              <Typography variant="h5" gutterBottom>
                Access Denied
              </Typography>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                You don't have permission to access this feature.
              </Typography>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 1,
                  backgroundColor: 'var(--surface-muted, #f5f5f5)',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  If you believe you should have access, please contact your
                  system administrator or request the required permissions.
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default SelfService
